// CustomersPage — back-office customer management.
//
// Visual language: Satori-style table with two-line cells, search-as-you-type,
// type filter chips, click-row → side panel detail with jobs history.
//
// Hot-path entry points:
//   1. The "+ Add customer" button on this page itself
//   2. The persistent "+ New customer" button in the AppShell header
//      (fires a custom DOM event we listen for here)
//
// Both open the same Add/Edit customer modal.

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Building2, Home, Briefcase as BriefcaseIcon, StickyNote, Check as CheckIcon, Loader2, AlertCircle } from 'lucide-react';
import { useSortable } from '../hooks/useSortable.js';
import { SortHeader } from '../components/SortHeader.js';
import { isBlockingError } from '../lib/isBlockingError.js';
import {
  getCustomers,
  postCustomer,
  patchCustomer,
  deleteCustomer,
  getJobs,
  getJobDetail,
} from '../api/client.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import type {
  Customer,
  CustomerType,
  CustomerCreateInput,
  CustomerUpdateInput,
  Job,
} from '@gutterinv/shared';
import { Card } from '../components/ui/card.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Skeleton } from '../components/ui/skeleton.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.js';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet.js';
import { cn } from '../lib/utils.js';
import { formatCents, formatDate } from '../lib/format.js';
import { JobStatusBadge } from '../components/JobStatusBadge.js';
import { qk } from '../lib/queryKeys.js';

const TYPE_FILTERS: ReadonlyArray<{ value: 'all' | CustomerType; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'residential', label: 'Residential' },
  { value: 'builder', label: 'Builder' },
  { value: 'commercial', label: 'Commercial' },
];

export function CustomersPage() {
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | CustomerType>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  // Listen for the persistent header button (+ New customer) — see AppShell.
  useEffect(() => {
    const handler = () => {
      setEditingCustomer(null);
      setCreateOpen(true);
    };
    window.addEventListener('open-customer-create', handler);
    return () => window.removeEventListener('open-customer-create', handler);
  }, []);

  const { data: customers, isLoading, error } = useQuery({
    queryKey: qk.customers.all(),
    queryFn: getCustomers,
  });

  const filtered = useMemo(() => {
    if (!customers) return [];
    const q = searchText.trim().toLowerCase();
    return customers.filter((c) => {
      const matchesType = typeFilter === 'all' || c.type === typeFilter;
      if (!matchesType) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        (c.email?.toLowerCase().includes(q) ?? false) ||
        (c.phone?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [customers, searchText, typeFilter]);

  type CustSortKey = 'name' | 'type' | 'phone' | 'email' | 'discount' | 'jobs';
  const custAccessor = useCallback((c: Customer, k: CustSortKey): string | number | null => {
    switch (k) {
      case 'name': return c.name;
      case 'type': return c.type;
      case 'phone': return c.phone;
      case 'email': return c.email;
      case 'discount': return c.discountPct;
      case 'jobs': return c.jobCount ?? 0;
    }
  }, []);
  const { sorted, sort, toggle } = useSortable(filtered, custAccessor);

  const selected = customers?.find((c) => c.id === selectedId) ?? null;

  if (isLoading) {
    return <CustomersSkeleton />;
  }

  if (isBlockingError(error, !!customers)) {
    return (
      <Card className="border-destructive p-6">
        <div className="font-semibold text-destructive">Failed to load customers</div>
        <div className="mt-2 text-sm text-muted-foreground">
          {error instanceof Error ? error.message : String(error)}
        </div>
      </Card>
    );
  }

  return (
    <div className="pt-6 space-y-5">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">
            {filtered.length} of {customers?.length ?? 0} customers
            {typeFilter !== 'all' && ` · ${typeFilter}`}
            {searchText && ` · matching "${searchText}"`}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditingCustomer(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Add customer
        </Button>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by name, phone, email..."
            className="h-9 pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TYPE_FILTERS.map((f) => {
            const active = typeFilter === f.value;
            return (
              <button
                key={f.value}
                onClick={() => setTypeFilter(f.value)}
                className={cn(
                  'h-8 rounded-md border px-3 text-[0.8125rem] font-medium transition-all duration-150',
                  // Pop-out on hover (lift + navy shadow), press back down on click.
                  'transform-gpu hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(27,42,63,0.18)] active:translate-y-0 active:shadow-sm',
                  active
                    ? 'border-primary bg-accent text-primary'
                    : 'border-border bg-card text-muted-foreground hover:bg-accent/40',
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50">
              <tr className="border-b border-border">
                <SortHeader label="Customer" active={sort.key === 'name'} dir={sort.dir} onClick={() => toggle('name')} />
                <SortHeader label="Type" active={sort.key === 'type'} dir={sort.dir} onClick={() => toggle('type')} />
                <SortHeader label="Contact" active={sort.key === 'phone'} dir={sort.dir} onClick={() => toggle('phone')} />
                <SortHeader label="Discount" active={sort.key === 'discount'} dir={sort.dir} onClick={() => toggle('discount')} align="right" />
                <SortHeader label="Jobs" active={sort.key === 'jobs'} dir={sort.dir} onClick={() => toggle('jobs')} align="right" />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <div className="text-sm font-medium text-muted-foreground">
                      No customers match
                    </div>
                    <div className="text-xs text-taupe">
                      Try clearing the search or filter
                    </div>
                  </td>
                </tr>
              ) : (
                sorted.map((c) => (
                  <CustomerRow
                    key={c.id}
                    customer={c}
                    onClick={() => setSelectedId(c.id)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail side panel */}
      <Sheet open={selectedId !== null} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent>
          {selected && (
            <CustomerDetailPanel
              customer={selected}
              onEdit={() => {
                setEditingCustomer(selected);
                setSelectedId(null);
                setCreateOpen(true);
              }}
              onDeleted={() => setSelectedId(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Create / Edit modal */}
      <CustomerFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        editing={editingCustomer}
      />
    </div>
  );
}

// ============================================================
// TABLE ROW
// ============================================================

function CustomerRow({
  customer,
  onClick,
}: {
  customer: Customer;
  onClick: () => void;
}) {
  const queryClient = useQueryClient();
  const prefetchCustomerJobs = () => {
    void queryClient.prefetchQuery({ queryKey: qk.jobs.all(), queryFn: getJobs, staleTime: 10_000 });
  };
  return (
    <tr
      onClick={onClick}
      onMouseEnter={prefetchCustomerJobs}
      className="cursor-pointer border-b border-border last:border-b-0 transition-colors hover:bg-primary/[0.08]"
    >
      <td className="px-4 py-3">
        <div className="text-[0.9375rem] font-semibold text-foreground">
          {customer.name}
        </div>
        {customer.contactName && (
          <div className="mt-0.5 text-xs text-muted-foreground">
            {customer.contactName}
          </div>
        )}
        {customer.notes && (
          <div className="mt-0.5 truncate text-xs text-taupe">{customer.notes}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <CustomerTypeBadge type={customer.type} />
      </td>
      <td className="px-4 py-3">
        <div className="text-[0.8125rem] text-muted-foreground">
          {customer.phone ?? <span className="text-taupe">—</span>}
        </div>
        <div className="truncate text-xs text-taupe">
          {customer.email ?? <span>—</span>}
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        {customer.discountPct > 0 ? (
          <span className="tnum text-sm font-semibold text-foreground">
            {customer.discountPct}%
          </span>
        ) : (
          <span className="text-taupe">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="tnum text-sm font-semibold text-foreground">
          {customer.jobCount ?? 0}
        </div>
      </td>
    </tr>
  );
}

// ============================================================
// TYPE BADGE
// ============================================================

function CustomerTypeBadge({ type }: { type: CustomerType }) {
  const Icon =
    type === 'residential' ? Home : type === 'builder' ? Building2 : BriefcaseIcon;
  const variant: 'default' | 'secondary' | 'outline' =
    type === 'builder' ? 'default' : type === 'commercial' ? 'secondary' : 'outline';
  return (
    <Badge variant={variant} className="gap-1 capitalize">
      <Icon className="h-3 w-3" />
      {type}
    </Badge>
  );
}

// ============================================================
// DETAIL PANEL (slide-out sheet)
// ============================================================

function CustomerDetailPanel({
  customer,
  onEdit,
  onDeleted,
}: {
  customer: Customer;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Inline jobs history for this customer. Filtered client-side from
  // the full /api/jobs list — sufficient for v1; if jobs grow large
  // we'd add a /api/customers/:id/jobs endpoint instead.
  const { data: jobs, isLoading: jobsLoading } = useQuery({
    queryKey: qk.jobs.all(),
    queryFn: getJobs,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCustomer(customer.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.customers.all() });
      setConfirmDelete(false);
      onDeleted();
    },
  });

  // Inline notes: mirror the JobDetailPage pattern — textarea bound to
  // local draft state, autosave on blur, subtle saving/saved indicator.
  // Empty string serializes as null on save so the column clears.
  const [notesDraft, setNotesDraft] = useState(customer.notes ?? '');
  useEffect(() => {
    setNotesDraft(customer.notes ?? '');
  }, [customer.notes]);
  // Save-state feedback for the autosave (saving → saved-fades → error-persists).
  type SaveState = 'idle' | 'saving' | 'saved' | 'error';
  const [notesSaveState, setNotesSaveState] = useState<SaveState>('idle');
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A pending saved/error indicator belongs to the customer being shown — reset it (and
  // its fade timer) when the panel is reused for a different customer, and on unmount.
  useEffect(() => {
    setNotesSaveState('idle');
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, [customer.id]);
  useEffect(
    () => () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    },
    [],
  );
  const notesMutation = useMutation({
    mutationFn: (notes: string) =>
      patchCustomer(customer.id, { notes: notes.length === 0 ? null : notes }),
    onMutate: () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      setNotesSaveState('saving');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      setNotesSaveState('saved');
      savedTimerRef.current = setTimeout(() => setNotesSaveState('idle'), 1500);
    },
    // Persist the error until the next save attempt succeeds (onMutate clears it).
    onError: () => setNotesSaveState('error'),
  });

  const customerJobs = useMemo(
    () =>
      (jobs ?? [])
        .filter((j) => j.customerId === customer.id)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [jobs, customer.id],
  );

  return (
    <>
      <SheetHeader>
        <div className="flex items-center gap-3">
          <CustomerTypeBadge type={customer.type} />
          {customer.discountPct > 0 && (
            <Badge variant="secondary" className="tnum">
              {customer.discountPct}% discount
            </Badge>
          )}
        </div>
        <SheetTitle>{customer.name}</SheetTitle>
        {customer.contactName && (
          <div className="text-sm text-muted-foreground">
            Contact: {customer.contactName}
          </div>
        )}
        <SheetDescription>
          Customer since {formatDate(customer.createdAt)}
        </SheetDescription>
      </SheetHeader>

      <SheetBody className="space-y-6">
        {/* Contact */}
        <section>
          <h3 className="eyebrow mb-2">Contact</h3>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Phone</dt>
              <dd className="text-foreground">
                {customer.phone ?? <span className="text-taupe">—</span>}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Email</dt>
              <dd className="truncate text-foreground">
                {customer.email ?? <span className="text-taupe">—</span>}
              </dd>
            </div>
          </dl>
        </section>

        {/* Notes — inline editable, autosaves on blur. Mirrors the
            JobDetailPage notes pattern so the office has one mental
            model across entities. */}
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="eyebrow flex items-center gap-1.5">
              <StickyNote className="h-3 w-3" />
              Notes
            </h3>
            <div className="flex items-center gap-1 text-[0.625rem]">
              {notesSaveState === 'saving' ? (
                <span className="flex items-center gap-1 text-taupe">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving…
                </span>
              ) : notesSaveState === 'saved' ? (
                <span className="flex items-center gap-1 text-primary animate-in fade-in">
                  <CheckIcon className="h-3 w-3" />
                  Saved
                </span>
              ) : notesSaveState === 'error' ? (
                <span className="flex items-center gap-1 text-destructive">
                  <AlertCircle className="h-3 w-3" />
                  Couldn&rsquo;t save
                </span>
              ) : null}
            </div>
          </div>
          <textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={() => {
              const current = customer.notes ?? '';
              if (notesDraft !== current) notesMutation.mutate(notesDraft);
            }}
            placeholder="Add notes about this customer."
            className="min-h-[100px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm font-mono leading-relaxed ring-offset-background placeholder:text-taupe focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </section>

        {/* Jobs history */}
        <section>
          <h3 className="eyebrow mb-2">Job history</h3>
          {jobsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : customerJobs.length === 0 ? (
            <div className="rounded-md border border-border bg-secondary/30 px-3 py-6 text-center text-sm text-muted-foreground">
              No jobs yet
            </div>
          ) : (
            <div className="divide-y divide-border rounded-md border border-border">
              {customerJobs.map((j) => (
                <JobHistoryRow key={j.id} job={j} />
              ))}
            </div>
          )}
        </section>
      </SheetBody>

      <SheetFooter className="flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-30"
          onClick={() => setConfirmDelete(true)}
          disabled={customerJobs.length > 0}
          title={
            customerJobs.length > 0
              ? `Cannot delete — ${customerJobs.length} job${customerJobs.length === 1 ? '' : 's'} on file`
              : 'Delete customer'
          }
        >
          Delete
        </Button>
        <Button variant="default" onClick={onEdit}>
          Edit customer
        </Button>
      </SheetFooter>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete customer?"
        description={
          <>
            <span className="font-semibold text-foreground">{customer.name}</span>{' '}
            will be permanently removed. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        variant="destructive"
        isPending={deleteMutation.isPending}
        error={
          deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : null
        }
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => {
          setConfirmDelete(false);
          deleteMutation.reset();
        }}
      />
    </>
  );
}

function JobHistoryRow({ job }: { job: Job }) {
  const queryClient = useQueryClient();
  const prefetchJob = () => {
    void queryClient.prefetchQuery({ queryKey: qk.jobs.detail(job.id), queryFn: () => getJobDetail(job.id), staleTime: 10_000 });
  };
  return (
    <Link to={`/jobs/${job.id}`} onMouseEnter={prefetchJob} className="flex items-center justify-between gap-3 px-3 py-2.5 transition-colors hover:bg-primary/[0.06] no-underline">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">
          {job.displayName}
        </div>
        <div className="truncate text-xs text-taupe">
          {formatDate(job.createdAt)}
          {job.scheduledStartDate && ` · install ${job.scheduledStartDate}`}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {job.quoteAmountCents !== null && (
          <span className="tnum text-sm font-semibold text-foreground">
            {formatCents(job.quoteTotalCents)}
          </span>
        )}
        <JobStatusBadge status={job.status} />
      </div>
    </Link>
  );
}

// ============================================================
// CREATE / EDIT MODAL
// ============================================================

function CustomerFormDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: Customer | null;
}) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<CustomerType>('residential');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [discountPct, setDiscountPct] = useState('0');
  const [notes, setNotes] = useState('');

  // Reset form whenever modal opens — copy from editing target if present.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setType(editing.type);
      setName(editing.name);
      setCompanyName(editing.companyName ?? '');
      setContactName(editing.contactName ?? '');
      setEmail(editing.email ?? '');
      setPhone(editing.phone ?? '');
      setDiscountPct(String(editing.discountPct));
      setNotes(editing.notes ?? '');
    } else {
      setType('residential');
      setName('');
      setCompanyName('');
      setContactName('');
      setEmail('');
      setPhone('');
      setDiscountPct('0');
      setNotes('');
    }
  }, [open, editing]);

  const createMutation = useMutation({
    mutationFn: (input: CustomerCreateInput) => postCustomer(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.customers.all() });
      onOpenChange(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CustomerUpdateInput }) =>
      patchCustomer(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.customers.all() });
      onOpenChange(false);
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error ?? updateMutation.error;

  const isOrg = type !== 'residential';
  const trimmedName = name.trim();
  const trimmedCompany = companyName.trim();
  const trimmedContact = contactName.trim();
  const canSave = isOrg ? trimmedCompany.length > 0 : trimmedName.length > 0;

  const handleSave = () => {
    if (!canSave) return;
    const parsedDiscount = Math.round(Number(discountPct));
    if (Number.isNaN(parsedDiscount) || parsedDiscount < 0 || parsedDiscount > 100) {
      return;
    }
    const payload: CustomerCreateInput = {
      type,
      name: isOrg ? trimmedCompany : trimmedName,
      companyName: isOrg ? trimmedCompany : null,
      contactName: isOrg ? trimmedContact || null : null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      discountPct: parsedDiscount,
      notes: notes.trim() || null,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, input: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit customer' : 'New customer'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Update contact info, type, or discount.'
              : 'Add a customer to the directory. Phone and email are optional.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Type selector — segmented buttons */}
          <div>
            <label className="eyebrow mb-1.5 block">Type</label>
            <div className="grid grid-cols-3 gap-1.5">
              {(['residential', 'builder', 'commercial'] as CustomerType[]).map((t) => {
                const active = type === t;
                const Icon = t === 'residential' ? Home : t === 'builder' ? Building2 : BriefcaseIcon;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={cn(
                      'flex h-10 items-center justify-center gap-2 rounded-md border text-sm font-medium capitalize transition-colors',
                      active
                        ? 'border-primary bg-accent text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-accent/40',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Name fields — single for residential, split for builder/commercial */}
          {isOrg ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="customer-company" className="eyebrow mb-1.5 block">
                  Company name *
                </label>
                <Input
                  id="customer-company"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Builders"
                  autoFocus
                  disabled={isSaving}
                />
              </div>
              <div>
                <label htmlFor="customer-contact" className="eyebrow mb-1.5 block">
                  Primary contact
                </label>
                <Input
                  id="customer-contact"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                  disabled={isSaving}
                />
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="customer-name" className="eyebrow mb-1.5 block">
                Name *
              </label>
              <Input
                id="customer-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. John Doe"
                autoFocus
                disabled={isSaving}
              />
            </div>
          )}

          {/* Phone + Email side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="customer-phone" className="eyebrow mb-1.5 block">
                Phone
              </label>
              <Input
                id="customer-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="555-0100"
                disabled={isSaving}
              />
            </div>
            <div>
              <label htmlFor="customer-email" className="eyebrow mb-1.5 block">
                Email
              </label>
              <Input
                id="customer-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@example.com"
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Discount */}
          <div>
            <label htmlFor="customer-discount" className="eyebrow mb-1.5 block">
              Discount (%)
            </label>
            <Input
              id="customer-discount"
              type="number"
              min="0"
              max="100"
              step="1"
              value={discountPct}
              onChange={(e) => {
                // Defensive: round any pasted decimal to a whole number
                // so backend Zod .int() never rejects what the UI accepted.
                const raw = e.target.value;
                if (raw === '') {
                  setDiscountPct('');
                  return;
                }
                const n = Number(raw);
                if (Number.isNaN(n)) return;
                setDiscountPct(String(Math.round(n)));
              }}
              className="w-32"
              disabled={isSaving}
            />
            <p className="mt-1 text-xs text-taupe">
              Whole-number percent applied to all quotes for this customer
            </p>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="customer-notes" className="eyebrow mb-1.5 block">
              Notes
            </label>
            <textarea
              id="customer-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything worth remembering"
              rows={3}
              disabled={isSaving}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error instanceof Error ? error.message : 'Save failed'}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !canSave}>
            {isSaving ? 'Saving…' : editing ? 'Save changes' : 'Create customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// HELPERS
// ============================================================

function CustomersSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-9 w-48" />
      <Skeleton className="h-9 w-full max-w-md" />
      <Skeleton className="h-96 w-full" />
    </div>
  );
}
